import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_gym-roster-pro-1/artifacts/2uba3mjm_goat.jpg";

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const navigate = useNavigate();
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success('Account created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-wider">Back to Home</span>
          </Link>

          <div className="mb-8">
            <img 
              src={LOGO_URL} 
              alt="G.O.A.T Fitness" 
              className="h-16 w-auto"
            />
          </div>

          <h1 className="font-['Barlow_Condensed'] font-extrabold text-4xl uppercase tracking-tight text-white mb-2">
            {isLogin ? 'Owner Login' : 'Create Account'}
          </h1>
          <p className="text-zinc-400 mb-8">
            {isLogin 
              ? 'Access your gym management dashboard' 
              : 'Set up your gym management system'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="form-group">
                <Label className="form-label">Full Name</Label>
                <Input
                  data-testid="register-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="input-dark"
                  required
                />
              </div>
            )}

            <div className="form-group">
              <Label className="form-label">Email Address</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="owner@goatfitness.com"
                className="input-dark"
                required
              />
            </div>

            <div className="form-group">
              <Label className="form-label">Password</Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  className="input-dark pr-12"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider py-6 rounded-none shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)]"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
            </Button>
          </form>

          <p className="text-center text-zinc-400 mt-8">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            <button
              data-testid="toggle-auth-mode-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#FF3B30] hover:text-white ml-2 font-medium transition-colors"
            >
              {isLogin ? 'Register' : 'Login'}
            </button>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=80)',
            filter: 'grayscale(80%)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="font-['Barlow_Condensed'] font-bold text-3xl text-white uppercase tracking-tight mb-4">
            "The only bad workout is the one that didn't happen."
          </blockquote>
          <p className="text-zinc-400">- Goat Fitness Motto</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
